import { useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Award, TrendingUp, AlertTriangle, Sparkles, ChevronRight } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { api } from '../api.js';
import { Card, CardHeader, Badge, ScoreBadge, PageHeader, Spinner } from '../components/ui.jsx';

export default function ResultsPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const justCompleted = params.get('just_completed') === '1';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['results', id],
    queryFn: () => api.getResults(id),
    refetchInterval: (q) => q.state.data?.report_pending ? 5000 : false
  });

  if (isLoading) return <div className="grid place-items-center min-h-[60vh]"><Spinner className="w-8 h-8 text-primary-600" /></div>;
  if (!data) return null;

  const session = data.session;
  const score = data.score;
  const report = data.report;
  const competencies = score?.competency_scores || {};
  const compArray = Object.entries(competencies)
    .map(([k, v]) => ({ key: k, label: (session.module.competencies || []).find(c => c.key === k)?.label || k, score: v.score }));
  const sortedComp = [...compArray].sort((a, b) => b.score - a.score);
  const strengths = sortedComp.slice(0, 3);
  const development = [...sortedComp].reverse().slice(0, 3);

  return (
    <>
      {justCompleted ? (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-lg bg-gradient-to-br from-success-soft to-primary-50 border border-success/30 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-success grid place-items-center text-white"><Award className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-semibold text-ink-900">Assessment complete</div>
            <div className="text-sm text-ink-600">Your scores are below. {data.report_pending ? 'Your personalised report is generating — refresh in a moment.' : 'Your personalised report is ready.'}</div>
          </div>
        </motion.div>
      ) : null}

      <PageHeader
        title={session.module.name}
        description={`Completed ${new Date(session.completed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
        action={
          score?.validity_severity && score.validity_severity !== 'none' ? (
            <Badge variant={score.validity_severity === 'high' ? 'red' : score.validity_severity === 'medium' ? 'amber' : 'grey'}>
              <AlertTriangle className="w-3 h-3 mr-1" /> Validity: {score.validity_severity}
            </Badge>
          ) : null
        } />

      {/* Hero score */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-1 text-center">
          <div className="text-xs uppercase tracking-wider font-semibold text-ink-500 mb-3">Overall capability</div>
          <ScoreBadge score={score?.overall || 0} large />
          <p className="text-sm text-ink-500 mt-3">
            {score?.benchmark_percentile ? (
              <>You scored higher than <strong className="text-ink-900">{score.benchmark_percentile}%</strong> of the {score.cohort_size > 100 ? 'workforce' : 'modeled'} cohort.</>
            ) : 'Benchmark data is being built up.'}
          </p>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Capability profile" subtitle="Your scores across each competency in this module" />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={compArray} outerRadius={88}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <Radar dataKey="score" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.18} strokeWidth={2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Competency bar */}
      <Card className="mb-6">
        <CardHeader title="Competency breakdown" subtitle="Sorted from strongest to weakest" />
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sortedComp} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: '#475569' }} width={140} />
              <Tooltip cursor={{ fill: '#F1F5F9' }} />
              <Bar dataKey="score" fill="url(#barGrad)" radius={[0, 6, 6, 0]} />
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#1E40AF" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Strengths / development */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader title="Strengths" subtitle="Where you stand out" />
          <ul className="space-y-3">
            {strengths.map(s => (
              <li key={s.key} className="flex items-center gap-3">
                <ScoreBadge score={s.score} />
                <div className="flex-1">
                  <div className="font-medium text-ink-900">{s.label}</div>
                </div>
                <Sparkles className="w-4 h-4 text-accent-500" />
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader title="Development priorities" subtitle="Where focused effort will move the needle" />
          <ul className="space-y-3">
            {development.map(d => (
              <li key={d.key} className="flex items-center gap-3">
                <ScoreBadge score={d.score} />
                <div className="flex-1">
                  <div className="font-medium text-ink-900">{d.label}</div>
                </div>
                <TrendingUp className="w-4 h-4 text-primary-500" />
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* AI report */}
      <Card>
        <CardHeader title="Your personal report" subtitle={report ? `Generated ${new Date(report.generated_at).toLocaleString('en-AU')}` : 'Being prepared by AI'} />
        {data.report_pending && !report ? (
          <div className="py-8 text-center">
            <Spinner className="w-8 h-8 text-primary-500 mx-auto mb-3" />
            <p className="text-sm text-ink-500">Your personal report is being generated. This usually takes 10-30 seconds.</p>
          </div>
        ) : report ? (
          <ReportContent content={report.content} />
        ) : (
          <p className="text-sm text-ink-500">No report available yet.</p>
        )}
      </Card>
    </>
  );
}

function ReportContent({ content }) {
  return (
    <div className="space-y-5 prose prose-sm max-w-none">
      <p className="text-lg font-semibold text-ink-900 leading-snug">{content.headline}</p>
      <p className="text-ink-700 leading-relaxed">{content.opening}</p>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <h4 className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-accent-500" />Your strengths</h4>
          <p className="text-sm text-ink-700 leading-relaxed">{content.strengths_narrative}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-primary-500" />Where to focus</h4>
          <p className="text-sm text-ink-700 leading-relaxed">{content.development_narrative}</p>
        </div>
      </div>

      {content.suggested_focus_areas?.length ? (
        <div>
          <h4 className="text-sm font-semibold text-ink-900 mb-2">Suggested focus areas</h4>
          <ul className="space-y-1.5">
            {content.suggested_focus_areas.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-700"><ChevronRight className="w-4 h-4 mt-0.5 text-accent-500 shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {content.learning_path_suggestions?.length ? (
        <div className="bg-gradient-to-br from-primary-50/60 to-accent-50/60 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-ink-900 mb-2">Practical next steps</h4>
          <ul className="space-y-2">
            {content.learning_path_suggestions.map((s, i) => (
              <li key={i} className="text-sm text-ink-700 leading-relaxed">• {s}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {content.closing_note ? <p className="text-sm text-ink-500 italic">{content.closing_note}</p> : null}
    </div>
  );
}
